import React, { useCallback, useMemo, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import GenericFormDisplay from "domains/3d/components/GenericFormDisplay";
import { useCustomInference } from "domains/3d/hooks/useCustomInference";
import useImportParamsCustomInference from "domains/3d/hooks/useImportParamsCustomInference";
import { getIsVeo3Model } from "domains/3d/utils";
import { CardActionsVideo } from "domains/assets/components/CardActions";
import { ListActionsVideo } from "domains/assets/components/ListActions";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FILTER_VIDEO_TYPES_KEYS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import InferenceGeneratorContent, {
  InferenceGeneratorTabsHandle,
} from "domains/inference/components/InferenceGenerator/Content";
import SelectModel from "domains/inference/components/InferenceGenerator/Sidebar/SelectModel";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import { SectionsProvider } from "domains/inference/contexts/SectionsProvider";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import Icon from "domains/ui/components/Icon";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";

import { HStack, Text } from "@chakra-ui/react";

export default function GenerateVideoPage() {
  return (
    <>
      <NextSeo title="Video" />

      <SectionsProvider>
        <JobSessionProvider
          forcedFilterTypeOptions={[...FILTER_VIDEO_TYPES_KEYS]}
          placeholderAssetType="img2video"
        >
          <GenerateVideo />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

export function GenerateVideo() {
  const {
    handleGenerate,
    isGenerating,
    isDisabled,
    cuCost,
    isCuLoading,
    form,
    setForm,
    model,
    setModel,
    disabledReasons,
  } = useCustomInference({
    type: "video",
  });
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<InferenceGeneratorTabsHandle>(null);
  const [selectModelIsOpen, setSelectModelIsOpen] = useState(false);
  const hasVeo3Flag = !!useUserContext().featureFlags?.includes("veo3");

  const { hasFailedToLoadModel } = useImportParamsCustomInference({
    model,
    setModel,
    setForm,
    type: "video",
  });

  const isModelUnavailable = useMemo(
    () => getIsVeo3Model(model) && !hasVeo3Flag,
    [model, hasVeo3Flag]
  );

  const onSubmit = useCallback(async () => {
    if (isModelUnavailable) {
      return;
    }
    const job = await handleGenerate();
    if (job) {
      addJob(job);
      tabsRef.current?.reset();
    }
  }, [handleGenerate, addJob, isModelUnavailable]);

  // ----------------------------------

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
  });

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={onSubmit}>
      <GeneratorSidebar>
        <SidebarTitle
          title="Generate Video"
          videoUrl="https://www.youtube.com/embed/urDgfDn7GhY?si=aoMPWibwkhfDig5v"
          videoTooltip="Watch a quick video to learn about generating videos"
        />
        <Divider />

        <SelectModel
          variant="video"
          isOpen={selectModelIsOpen}
          setIsOpen={setSelectModelIsOpen}
          model={model}
          setModel={setModel}
          isLoadingModel={!model && !hasFailedToLoadModel}
        />
        <ModelMessage model={model} />
        <Divider />

        <GenericFormDisplay
          form={form}
          setForm={setForm}
          model={model}
          triggerButtonProps={{
            onClick: onSubmit,
            isDisabled: isDisabled || isModelUnavailable,
            isCuLoading: isCuLoading,
            cuCost: cuCost,
            isGenerating: isGenerating,
          }}
          type="video"
          disabledReasons={disabledReasons}
          isLoadingModel={!model && !hasFailedToLoadModel}
        />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <InferenceGeneratorContent
          type="video"
          scrollRef={scrollRef}
          tabsRef={tabsRef}
          CardActionsComponent={CardActionsVideo}
          suggestArgs={
            form.prompt || model
              ? { prompt: form.prompt as string, model }
              : undefined
          }
        >
          <FileManagerImage
            variant="video"
            scrollRef={scrollRef}
            files={
              !files?.length && !jobs?.length && !isLoading
                ? placeholders
                : files
            }
            jobs={jobs}
            isSelectable
            isLoading={isLoading}
            {...fileManagerProps}
            ListActionsComponent={ListActionsVideo}
            CardActionsComponent={CardActionsVideo}
            styleProps={FM_HEADER_TOP_0}
          />
        </InferenceGeneratorContent>
      </GeneratorContent>
    </GeneratorContainer>
  );
}

function ModelMessage({
  model,
}: {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
}) {
  const hasVeo3Flag = !!useUserContext().featureFlags?.includes("veo3");

  const message = useMemo(() => {
    if (getIsVeo3Model(model) && !hasVeo3Flag) {
      return "Veo3 access is currently limited, please reach out to support for more information.";
    }
  }, [model, hasVeo3Flag]);

  if (!message) return null;

  return (
    <HStack
      align="start"
      pt={2}
      pb={4}
      px={4}
      color="textSecondary"
      spacing={2}
    >
      <Icon id="Toast/Info" h="16px" mt={1} />
      <Text size="body.md">{message}</Text>
    </HStack>
  );
}
